import { useProperty } from '../../api/properties.ts';
import { useClientReports, type SectionKey } from '../../api/navigation.ts';
import {
  useCommissionOrders, KIND_WORDS as ORDER_KIND_WORDS, type CommissionOrder, type OrderRecordKind,
} from '../../api/orderRecords.ts';
import { useRecordDocument } from '../RecordDocument.tsx';
import type { ClosedByRecord } from '../../api/tasks.ts';
import { formatDate } from '../../dates.ts';
import { formatInstant } from '../../tasks/fields.ts';
import { SiteVisitHistory } from '../SiteVisits.tsx';
import { VisitDetails } from '../VisitDetails.tsx';
import { DATE_WORD, ORDER_DATE } from '../OrderRecords.tsx';
import { KIND_SECTION } from '../../activity.ts';
import { BondFacts } from '../BondsPanel.tsx';
import { useClientCommission } from '../../api/commissions.ts';

/**
 * A-#205 (Arthur, 27 October 2026, "render the whole site visit thing in there"): the record that closed a Done task,
 * IN the task, read only, as the record itself shows it. Each record is read through its own route, as the reader,
 * and only where the reader holds the section that shows it (the same rule as its link): nothing is shown here that
 * the reader could not open. Editing stays on the record's own place ("Open full screen").
 */
export function closedRecordSection(kind: ClosedByRecord['kind']): SectionKey {
  // deploy 8 (P-13): the one map, shared with the Timeline and closedRecordLink()
  return KIND_SECTION[kind]!;
}

export function ClosedRecordFacts({ clientId, r, sections }: { clientId: string; r: ClosedByRecord; sections: readonly SectionKey[] }) {
  if (!sections.includes(closedRecordSection(r.kind))) return null;
  if (r.kind === 'site_visit') return r.property_id ? <SiteVisitFacts propertyId={r.property_id} visitId={r.id} /> : null;
  if (r.kind === 'filing') return r.deadline_id ? <FilingFacts clientId={clientId} deadlineId={r.deadline_id} /> : null;
  if (r.kind === 'visit') return <VisitDetails id={r.id} readOnly />;
  // 162 (P-35): the order's bond, read only (r.id is the order's)
  if (r.kind === 'bond') return <BondFacts clientId={clientId} orderId={r.id} />;
  // 167 (P-24): the blocker the record cleared, read only (r.id is the blocker's)
  if (r.kind === 'commission_blocker') return <BlockerFacts clientId={clientId} blockerId={r.id} />;
  // 173 (P-46): a marshalling step: the card's line says the step, its bank and its day; its facts stay on the Finance
  // section's panel (the link), never the order's
  if (r.kind === 'marshalling') return null;
  return <OrderFacts clientId={clientId} orderId={r.id} kind={r.kind} />;
}

export const Fact = ({ term, children }: { term: string; children: React.ReactNode }) => (
  <div className="flex flex-wrap gap-x-3 py-0.5"><dt className="w-36 shrink-0 text-sm font-semibold text-neutral-700">{term}</dt>
    <dd className="min-w-0 flex-1 text-neutral-900">{children}</dd></div>
);
export const Facts = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <dl aria-label={label} className="mt-2 divide-y divide-neutral-100">{children}</dl>
);
const minutes = (m: number | null) => (m === null ? 'Not recorded' : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`);

export function SiteVisitFacts({ propertyId, visitId }: { propertyId: string; visitId: string }) {
  const q = useProperty(propertyId);
  const v = q.data?.visits.find((x) => x.id === visitId);
  if (!q.data) return <p className="mt-2 text-sm text-neutral-600">{q.error ? 'The site visit could not be read.' : 'Loading the site visit…'}</p>;
  if (!v) return <p className="mt-2 text-sm text-neutral-700">The site visit is not on the property's record any more.</p>;
  return (
    <>
      <Facts label="The site visit">
        <Fact term="Visited">{formatDate(v.visited_on)}</Fact>
        <Fact term="Who visited">{v.visited_by ?? 'Not recorded'}</Fact>
        <Fact term="Time spent">{minutes(v.duration_minutes)}</Fact>
        {v.documents_retrieved && <Fact term="Documents">Documents were retrieved</Fact>}
        <Fact term="Note">{v.note ? <span className="whitespace-pre-wrap">{v.note}</span> : 'None'}</Fact>
        <Fact term="Recorded">{v.recorded_by ?? 'Someone not on record'}, {formatInstant(v.created_at)}</Fact>
        {v.withdrawn && <Fact term="Withdrawn">{v.withdrawal_reason ?? 'Withdrawn'}</Fact>}
      </Facts>
      {v.edits > 0 && <SiteVisitHistory id={v.id} />}
    </>
  );
}

export function FilingFacts({ clientId, deadlineId }: { clientId: string; deadlineId: string }) {
  const q = useClientReports(clientId);
  const row = q.data?.reports.find((x) => x.deadline_id === deadlineId);
  if (!q.data) return <p className="mt-2 text-sm text-neutral-600">{q.error ? 'The filing could not be read.' : 'Loading the filing…'}</p>;
  if (!row) return <p className="mt-2 text-sm text-neutral-700">The report is not on the client's record any more.</p>;
  return (
    <Facts label="The filing">
      <Fact term="Report">{row.label}{row.period_year ? ` · ${row.period_year}` : ''}</Fact>
      <Fact term="Filed">{row.filed_date ? formatDate(row.filed_date) : row.filed_date_unknown ? 'Filed, date not known' : 'Not recorded'}</Fact>
      <Fact term="Recorded">{row.recorded_by ?? 'Someone not on record'}{row.recorded_at ? `, ${formatInstant(row.recorded_at)}` : ''}</Fact>
    </Facts>
  );
}

export function OrderFacts({ clientId, orderId, kind, missing }: {
  clientId: string; orderId: string; kind: 'commission' | 'oath_designation';
  /** deploy 8: shown instead, where the order is not one the Commission panel lists (a superseded order, A-#219) */
  missing?: React.ReactNode;
}) {
  const q = useCommissionOrders(clientId);
  const o = q.data?.orders.find((x) => x.id === orderId);
  if (!q.data) return <p className="mt-2 text-sm text-neutral-600">{q.error ? 'The order could not be read.' : 'Loading the order…'}</p>;
  if (!o) return missing ?? <p className="mt-2 text-sm text-neutral-700">The order is not on the client's record any more.</p>;
  return <OrderFactsOf clientId={clientId} order={o} kind={kind} />;
}

function OrderFactsOf({ clientId, order: o, kind }: { clientId: string; order: CommissionOrder; kind: OrderRecordKind }) {
  const rec = o.records.find((x) => x.kind === kind && !x.withdrawn) ?? null;
  const date = ORDER_DATE[kind](o);
  // P-20: the document is a "View document" button or words, never a bare file name that looks clickable
  const recordDoc = useRecordDocument(clientId, rec, ORDER_KIND_WORDS[kind].toLowerCase());
  return (
    <>
      <Facts label={`The ${ORDER_KIND_WORDS[kind].toLowerCase()}`}>
        <Fact term="Order">{o.label ?? 'Order'}{o.signed_date ? `, signed ${formatDate(o.signed_date)}` : ''}</Fact>
        <Fact term={DATE_WORD[kind]}>{date ? formatDate(date) : 'A date not on the order'}</Fact>
        <Fact term="Document">{recordDoc.control}</Fact>
        <Fact term="Recorded by">{rec?.recorded_by ?? 'Not recorded here'}</Fact>
      </Facts>
      {recordDoc.preview}
    </>
  );
}

/** 167 (P-24, Q-D): a commission blocker the record cleared: its reason, words, and how and when it cleared. */
function BlockerFacts({ clientId, blockerId }: { clientId: string; blockerId: string }) {
  const q = useClientCommission(clientId);
  const b = q.data?.blockers.find((x) => x.id === blockerId);
  if (!b) return null;
  return (
    <dl className="mt-2">
      <Fact term="Blocker">{b.reason_label}: {b.note}</Fact>
      <Fact term="Opened">{formatDate(b.opened_on)}</Fact>
      {b.cleared_on && <Fact term="Cleared">{formatDate(b.cleared_on)}{b.cleared_note ? ` (${b.cleared_note})` : ''}</Fact>}
    </dl>
  );
}
